import * as THREE from "three";
import { cellShape, cellTile, makeGrid, pieceBox, pieceOf, pieceParts, setShape, shapeAt, shapeTile, turnShape } from "@dyson/shared";
import { GridMesh, tileMaterial } from "./GridMesh";

/** Tints for a placement that would go ahead, or be refused. */
const OK = new THREE.Color(0.75, 1, 0.7);
const REFUSED = new THREE.Color(1, 0.45, 0.4);

/**
 * A faint copy of what right click would place, drawn where it would go: one
 * tile, or a whole piece from its anchor. Positioned like the build cursor.
 */
export class Ghost {
  private readonly material = tileMaterial.clone();
  private readonly grid = new GridMesh(this.material);
  private cell: number | null = null;
  private turn = 0;

  constructor() {
    this.material.transparent = true;
    this.material.opacity = 0.5;
    this.material.depthWrite = false;
    this.grid.mesh.renderOrder = 18;
    this.grid.mesh.visible = false;
  }

  get object(): THREE.Object3D {
    return this.grid.mesh;
  }

  /**
   * Show `cell` (a tile and its shape, see packCell; null hides it), tinted
   * by whether placing it would be allowed, turned by `turn` quarter turns
   * (on a rock that turns, see turnAt: a piece's footprint, a shape's slope).
   * Drawn from the lowest corner of what it would cover, so it sits on the
   * cursor.
   */
  show(cell: number | null | undefined, ok: boolean, turn = 0): void {
    this.grid.mesh.visible = cell != null;
    if (cell == null) return;
    this.material.color.copy(ok ? OK : REFUSED);
    if (cell === this.cell && turn === this.turn) return;
    this.cell = cell;
    this.turn = turn;
    const tile = cellTile(cell);
    const def = pieceOf(tile)?.def;
    const box = def ? pieceBox(def, turn) : { x0: 0, y0: 0, w: 1, h: 1 };
    const g = makeGrid(box.w, box.h);
    if (def) for (const [dx, dy, part] of pieceParts(tile, turn)) g.tiles[(dy - box.y0) * g.w + dx - box.x0] = part;
    else {
      g.tiles[0] = tile;
      setShape(g, 0, turnShape(cellShape(cell), turn));
    }
    this.grid.rebuild(g, {
      orient: () => turn,
      shape: (x, y) => shapeTile(shapeAt(g, y * g.w + x)),
    });
  }

  dispose(): void {
    this.grid.dispose();
    this.material.dispose();
  }
}
